import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ifMatchFor } from '../api/client';
import { qk, useActuation, useDevices, useReadings, useRooms, useRoutines, useStates } from '../api/queries';
import type { Device, Mode, RadiatorIn, Room, SensorIn, Suspend } from '../api/types';
import { MODES } from '../api/types';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, KV, ModeChip, PageHeader, Segmented } from '../components/ui';
import { byId, coverage, deviceName, isRadiator, isSensor, mappingValid, roomName, stateFor } from '../lib/domain';
import { COMMAND_LABEL, MODE_LABEL, REASON_LABEL, WEEKDAY_SHORT } from '../lib/labels';
import { ago, dayTime, readingFreshness } from '../lib/time';
import { WEEKDAYS } from '../api/types';

type Mapping = Record<Mode, number[]>;
const EMPTY_MAPPING: Mapping = { confort: [0, 0], eco: [0, 0], hors_gel: [0, 0], arret: [0, 0] };

export function DevicesPage() {
  const { deviceId } = useParams();
  const devices = useDevices();
  const rooms = useRooms();
  const [creating, setCreating] = useState(false);
  const selected = devices.data?.find((d) => d.id === deviceId) ?? null;

  return (
    <>
      <PageHeader
        title="Radiateurs & pièces"
        subtitle={devices.data ? `${devices.data.filter(isRadiator).length} radiateur(s) · ${devices.data.filter(isSensor).length} sonde(s) · ${rooms.data?.length ?? 0} pièce(s)` : undefined}
        actions={<button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>Ajouter un device</button>}
      />
      <div className="split">
        <div className="split-main stack-l">
          <RoomsCard rooms={rooms.data} devices={devices.data} />
          <DevicesTable devices={devices.data} rooms={rooms.data} loading={devices.isLoading} error={devices.error} selectedId={deviceId} />
        </div>
        <aside className="split-side">
          {creating ? (
            <CreateDevice rooms={rooms.data ?? []} onDone={() => setCreating(false)} />
          ) : selected ? (
            <DeviceDetail key={selected.id} d={selected} rooms={rooms.data ?? []} />
          ) : (
            <Card title="Détail"><p className="muted small">Choisissez un device dans la liste pour le modifier, le calibrer ou le suspendre.</p></Card>
          )}
        </aside>
      </div>
    </>
  );
}

function RoomsCard({ rooms, devices }: { rooms?: Room[]; devices?: Device[] }) {
  const { run, busy } = useAction();
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const count = (id: string) => (devices ?? []).filter((d) => d.room_id === id).length;

  return (
    <Card title="Pièces">
      <div className="tile-grid">
        {(rooms ?? []).map((r) => (
          <div key={r.id} className={`tile ${r.reserved ? 'tile-dashed' : ''}`}>
            {editing?.id === r.id ? (
              <form
                className="stack-s"
                onSubmit={(e) => {
                  e.preventDefault();
                  run(() => api.send('PATCH', `/rooms/${r.id}`, { body: { name: editing.name }, ifMatch: ifMatchFor(`/rooms/${r.id}`, r.version) }), {
                    success: 'Pièce renommée', invalidate: [qk.rooms],
                  }).then(() => setEditing(null));
                }}
              >
                <label className="sr-only" htmlFor={`rn-${r.id}`}>Nouveau nom</label>
                <input id={`rn-${r.id}`} value={editing.name} onChange={(e) => setEditing({ id: r.id, name: e.target.value })} autoFocus />
                <div className="row gap-s">
                  <button className="btn btn-s btn-primary" disabled={busy || !editing.name.trim()}>Enregistrer</button>
                  <button type="button" className="btn btn-s" onClick={() => setEditing(null)}>Annuler</button>
                </div>
              </form>
            ) : (
              <>
                <div className="strong">{r.name}</div>
                <div className="small muted">{count(r.id)} device(s){r.reserved ? ' · pièce par défaut' : ''}</div>
                <div className="row gap-s small">
                  <button type="button" className="link" onClick={() => setEditing({ id: r.id, name: r.name })}>Renommer</button>
                  {!r.reserved && (
                    <button type="button" className="link link-bad" disabled={busy}
                      onClick={() => {
                        if (!window.confirm(`Supprimer « ${r.name} » ? Ses devices passeront dans la pièce par défaut et son assignation de routine sera supprimée.`)) return;
                        run(() => api.send('DELETE', `/rooms/${r.id}`, { ifMatch: ifMatchFor(`/rooms/${r.id}`, r.version) }), {
                          success: 'Pièce supprimée', invalidate: [qk.rooms, qk.devices, qk.routines, qk.state],
                        });
                      }}>
                      Supprimer
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        <form
          className="tile tile-dashed stack-s"
          onSubmit={(e) => {
            e.preventDefault();
            run(() => api.send('POST', '/rooms', { body: { name: name.trim() } }), { success: 'Pièce ajoutée', invalidate: [qk.rooms] }).then((r) => r && setName(''));
          }}
        >
          <label htmlFor="new-room" className="strong">Nouvelle pièce</label>
          <input id="new-room" placeholder="ex. Salle d’eau" value={name} onChange={(e) => setName(e.target.value)} />
          <button className="btn btn-s" disabled={busy || !name.trim()}>Ajouter</button>
        </form>
      </div>
    </Card>
  );
}

function DevicesTable({ devices, rooms, loading, error, selectedId }: { devices?: Device[]; rooms?: Room[]; loading: boolean; error: unknown; selectedId?: string }) {
  const states = useStates();
  const roomMap = byId(rooms);
  const nav = useNavigate();
  return (
    <Card title="Devices" className="card-flush">
      <Async q={{ data: devices, isLoading: loading, error }} empty={<p className="empty">Aucun device.</p>}>
        {(list) => (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr><th scope="col">Nom</th><th scope="col">Type</th><th scope="col">Pièce</th><th scope="col">État</th><th scope="col"><span className="sr-only">Action</span></th></tr>
              </thead>
              <tbody>
                {list.map((d) => {
                  const s = stateFor(states.data, d.id);
                  const f = readingFreshness(d.last_reading?.measured_at);
                  return (
                    <tr key={d.id} className={d.id === selectedId ? 'is-selected' : ''}>
                      <td><div className="strong">{deviceName(d)}</div><div className="mono small muted">{d.client_id}</div></td>
                      <td className="small">{isSensor(d) ? 'Sonde' : 'Radiateur 4 ordres'}</td>
                      <td>{roomName(roomMap, d.room_id)}</td>
                      <td>
                        {d.suspended ? <Badge tone="neutral">Suspendu</Badge>
                          : isSensor(d) ? (
                            d.last_reading ? <Badge tone={f === 'fresh' ? 'ok' : f === 'stale' ? 'warn' : 'bad'}>{d.last_reading.temperature_c.toFixed(1)} °C · {ago(d.last_reading.measured_at)}</Badge> : <Badge tone="neutral">Aucune mesure</Badge>
                          ) : s ? (
                            <div className="row gap-s wrap"><ModeChip mode={s.requested_mode} /><Badge tone={COMMAND_LABEL[s.command_status].tone}>{COMMAND_LABEL[s.command_status].label}</Badge></div>
                          ) : '—'}
                      </td>
                      <td className="right"><button type="button" className="btn btn-s" onClick={() => nav(`/radiateurs/${d.id}`)}>Éditer</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </Card>
  );
}

function RelayEditor({ value, onChange }: { value: Mapping; onChange: (m: Mapping) => void }) {
  const valid = mappingValid(value);
  return (
    <div className="stack-s">
      <table className="table table-compact">
        <thead><tr><th scope="col">Mode</th><th scope="col" className="mono">switch:0</th><th scope="col" className="mono">switch:1</th></tr></thead>
        <tbody>
          {MODES.map((m) => (
            <tr key={m}>
              <th scope="row"><ModeChip mode={m} /></th>
              {[0, 1].map((i) => (
                <td key={i}>
                  <button type="button" className={`relay ${value[m][i] ? 'is-on' : ''}`} aria-pressed={value[m][i] === 1} aria-label={`${MODE_LABEL[m]} switch:${i}`}
                    onClick={() => onChange({ ...value, [m]: value[m].map((v, j) => (j === i ? (v ? 0 : 1) : v)) })}>
                    {value[m][i]}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className={`alert small ${valid ? 'alert-ok' : 'alert-bad'}`} role="status">
        {valid ? '4 combinaisons distinctes · calibration valide' : 'Deux modes partagent la même combinaison : le service refusera la calibration.'}
      </p>
      <p className="small muted">0 = on:false, 1 = on:true. Aucune calibration par défaut : chaque combinaison doit être vérifiée sur l’installation.</p>
    </div>
  );
}

function DeviceDetail({ d, rooms }: { d: Device; rooms: Room[] }) {
  const { run, busy } = useAction();
  const nav = useNavigate();
  const [name, setName] = useState(d.name ?? '');
  const [roomId, setRoomId] = useState(d.room_id);
  const [mapping, setMapping] = useState<Mapping>((d.relay_mapping as Mapping | null) ?? EMPTY_MAPPING);
  const states = useStates();
  const s = stateFor(states.data, d.id);
  const routines = useRoutines();
  const path = `/devices/${d.id}`;
  const inv = [qk.devices, qk.state];

  useEffect(() => {
    setName(d.name ?? '');
    setRoomId(d.room_id);
  }, [d.version, d.name, d.room_id]);

  const patch = (body: object, success: string) =>
    run(() => api.send('PATCH', path, { body, ifMatch: ifMatchFor(path, d.version) }), { success, invalidate: inv });

  const suspend = (on: boolean) =>
    run(
      (confirmation) => api.send<Suspend>(on ? 'POST' : 'DELETE', `${path}/suspend`, { ifMatch: ifMatchFor(path, d.version), confirmation }).then((r) => r.data),
      { success: on ? 'Suspendu : Arrêt calibré sans expiration' : 'Suspension levée', invalidate: [...inv, qk.measurementStatus] },
    );

  return (
    <Card title={<><span className="eyebrow">{isSensor(d) ? 'Sonde de température' : 'Radiateur · actionneur'}</span>{deviceName(d)}</>}>
      <div className="stack">
        <form className="stack-s" onSubmit={(e) => { e.preventDefault(); patch({ name: name.trim() || null, room_id: roomId }, 'Device mis à jour'); }}>
          <div className="grid-2">
            <Field label="Nom" htmlFor="d-name"><input id="d-name" value={name} placeholder={d.client_id} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Pièce" htmlFor="d-room">
              <select id="d-room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
          </div>
          <p className="small muted">client_id <span className="mono">{d.client_id}</span> · type <span className="mono">{d.type}</span> (immuable)</p>
          <button className="btn" disabled={busy || (name === (d.name ?? '') && roomId === d.room_id)}>Enregistrer</button>
        </form>

        {isRadiator(d) && (
          <>
            {s && (
              <div className="panel">
                <div className="row gap-s wrap"><ModeChip mode={s.requested_mode} /> <Badge tone={COMMAND_LABEL[s.command_status].tone}>{COMMAND_LABEL[s.command_status].label}</Badge></div>
                <p className="small">{REASON_LABEL[s.reason]}</p>
              </div>
            )}
            <section className="stack-s" aria-labelledby="h-cal">
              <h3 id="h-cal" className="sub-title">Calibration fil pilote</h3>
              <RelayEditor value={mapping} onChange={setMapping} />
              <button type="button" className="btn btn-primary" disabled={busy || !mappingValid(mapping)}
                onClick={() => patch({ relay_mapping: mapping }, 'Calibration enregistrée : mode courant réémis')}>
                Enregistrer la calibration
              </button>
            </section>
            <section className="stack-s" aria-labelledby="h-mod">
              <h3 id="h-mod" className="sub-title">Module Shelly</h3>
              <KV items={[
                ['Driver', <span className="mono">shelly-rpc · Shelly2PMG3</span>],
                ['Préfixe MQTT', <span className="mono break">{d.config.actuator?.mqtt_topic_prefix ?? '—'}</span>],
                ['Sorties', <span className="mono">{(d.config.actuator?.relay_ids ?? [0, 1]).map((i) => `switch:${i}`).join(', ')}</span>],
                ['Connexion', s?.online === false ? 'Hors ligne' : s?.online ? 'En ligne' : 'Inconnue'],
              ]} />
            </section>
            <ActuationHistory id={d.id} />
            <section className="stack-s" aria-labelledby="h-cov">
              <h3 id="h-cov" className="sub-title">Routine suivie chaque jour</h3>
              {routines.data && (
                <div className="week-strip">
                  {coverage(d, routines.data).map((c, i) => (
                    <div key={WEEKDAYS[i]} className={`week-cell ${c.source ?? 'none'}`}>
                      <span className="small strong">{WEEKDAY_SHORT[WEEKDAYS[i]]}</span>
                      <span className="small">{c.routine ? c.routine.name : 'aucune'}</span>
                      <span className="xsmall muted">{c.source === 'device' ? 'radiateur' : c.source === 'room' ? 'pièce' : 'dernier mode / Arrêt'}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {isSensor(d) && <SensorDetail d={d} />}

        <section className="stack-s" aria-labelledby="h-susp">
          <h3 id="h-susp" className="sub-title">Suspension</h3>
          <p className="small">
            {isSensor(d)
              ? 'Exclut la sonde des mesures. Un test programmé qui l’utilise sera annulé.'
              : 'Force l’Arrêt calibré sans expiration, au-dessus de toute routine, forçage ou mesure.'}
          </p>
          {d.suspended
            ? <button type="button" className="btn" disabled={busy} onClick={() => suspend(false)}>Lever la suspension</button>
            : <button type="button" className="btn" disabled={busy} onClick={() => suspend(true)}>Suspendre</button>}
        </section>

        <div className="divider" />
        <button type="button" className="btn btn-danger" disabled={busy}
          onClick={() => {
            if (!window.confirm(`Supprimer « ${deviceName(d)} » ? Ses assignations et forçages individuels seront supprimés ; l’historique est conservé.`)) return;
            run(() => api.send('DELETE', path, { ifMatch: ifMatchFor(path, d.version) }), { success: 'Device supprimé', invalidate: [...inv, qk.routines] }).then((r) => r && nav('/radiateurs'));
          }}>
          Supprimer le device
        </button>
      </div>
    </Card>
  );
}

function ActuationHistory({ id }: { id: string }) {
  const q = useActuation(id);
  return (
    <section className="stack-s" aria-labelledby="h-act">
      <h3 id="h-act" className="sub-title">Dernières commandes RPC</h3>
      <Async q={{ data: q.data?.data, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Aucune commande envoyée.</p>}>
        {(rows) => (
          <ul className="timeline-list">
            {rows.map((a) => (
              <li key={a.rpc_id}>
                <span className="mono muted">{dayTime(a.sent_at)}</span>
                <span className="small">
                  switch:{a.relay_id} → {a.requested_on ? 'on' : 'off'} ({MODE_LABEL[a.requested_mode]}) · <span className={a.status === 'failed' ? 'text-bad' : ''}>{a.status}</span> · {a.attempts} essai(s)
                  {a.error && <span className="block text-bad">{a.error}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Async>
    </section>
  );
}

function SensorDetail({ d }: { d: Device }) {
  const q = useReadings(d.id);
  const f = readingFreshness(d.last_reading?.measured_at);
  return (
    <section className="stack-s" aria-labelledby="h-src">
      <h3 id="h-src" className="sub-title">Source de température</h3>
      <KV items={[
        ['Driver', <span className="mono">{d.config.sensor?.driver ?? 'nodemcu-mqtt-v1'}</span>],
        ['sensor_id', <span className="mono">{d.config.sensor?.sensor_id ?? '—'}</span>],
        ['Topic', <span className="mono break">{d.config.sensor?.mqtt_topic ?? `smart-energy-control/sensors/${d.config.sensor?.sensor_id ?? '…'}/temperature`}</span>],
        ['Dernière mesure acceptée', d.last_reading ? `${d.last_reading.temperature_c.toFixed(2)} °C · ${ago(d.last_reading.measured_at)}${f === 'stale' ? ' (périmée)' : f === 'lost' ? ' (perdue)' : ''}` : 'aucune'],
      ]} />
      <Async q={{ data: q.data?.data, isLoading: q.isLoading, error: q.error }} empty={<p className="small muted">Aucune mesure reçue.</p>}>
        {(rows) => (
          <ul className="timeline-list">
            {rows.map((r) => <li key={r.id}><span className="mono muted">{dayTime(r.observed_at)}</span><span className="mono">{r.temperature.toFixed(2)} °C</span></li>)}
          </ul>
        )}
      </Async>
    </section>
  );
}

function CreateDevice({ rooms, onDone }: { rooms: Room[]; onDone: () => void }) {
  const { run, busy } = useAction();
  const nav = useNavigate();
  const [kind, setKind] = useState<'radiator' | 'sensor'>('radiator');
  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState(rooms.find((r) => r.reserved)?.id ?? rooms[0]?.id ?? '');
  const [prefix, setPrefix] = useState('');
  const [sensorId, setSensorId] = useState('');
  const [topic, setTopic] = useState('');
  const [mapping, setMapping] = useState<Mapping>(EMPTY_MAPPING);

  const ok = clientId.trim() && (kind === 'radiator' ? prefix.trim() && mappingValid(mapping) : sensorId.trim());

  const submit = () => {
    const body: RadiatorIn | SensorIn = kind === 'radiator'
      ? { type: '4-ordres-2-relais', client_id: clientId.trim(), name: name.trim() || null, room_id: roomId, actuator: { driver: 'shelly-rpc', model: 'Shelly2PMG3', mqtt_topic_prefix: prefix.trim(), relay_ids: [0, 1] }, relay_mapping: mapping as RadiatorIn['relay_mapping'] }
      : { type: 'capteur-temperature', client_id: clientId.trim(), name: name.trim() || null, room_id: roomId, temperature_source: { driver: 'nodemcu-mqtt-v1', sensor_id: sensorId.trim(), ...(topic.trim() ? { mqtt_topic: topic.trim() } : {}) } };
    run(() => api.send<Device>('POST', '/devices', { body }).then((r) => r.data), {
      success: kind === 'radiator' ? 'Radiateur créé : le pilotage sera opérationnel après validation du module.' : 'Sonde créée',
      invalidate: [qk.devices, qk.state],
    }).then((dev) => {
      if (dev) {
        onDone();
        nav(`/radiateurs/${dev.id}`);
      }
    });
  };

  return (
    <Card title="Ajouter un device" actions={<button type="button" className="btn btn-s" onClick={onDone}>Fermer</button>}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (ok) submit(); }}>
        <Segmented label="Type" value={kind} onChange={setKind} options={[{ value: 'radiator', label: 'Radiateur' }, { value: 'sensor', label: 'Sonde' }]} />
        <div className="grid-2">
          <Field label="client_id" htmlFor="c-id" hint="Identifiant technique unique"><input id="c-id" value={clientId} onChange={(e) => setClientId(e.target.value)} required /></Field>
          <Field label="Nom (optionnel)" htmlFor="c-name"><input id="c-name" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        </div>
        <Field label="Pièce" htmlFor="c-room">
          <select id="c-room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        {kind === 'radiator' ? (
          <>
            <Field label="Préfixe MQTT du Shelly 2PM Gen3" htmlFor="c-prefix" hint="ex. shelly2pmg3-8cbfea9a1b10"><input id="c-prefix" className="mono" value={prefix} onChange={(e) => setPrefix(e.target.value)} required /></Field>
            <div className="field"><span className="label">Calibration (obligatoire)</span><RelayEditor value={mapping} onChange={setMapping} /></div>
          </>
        ) : (
          <div className="grid-2">
            <Field label="sensor_id" htmlFor="c-sid"><input id="c-sid" className="mono" value={sensorId} onChange={(e) => setSensorId(e.target.value)} required /></Field>
            <Field label="Topic MQTT (optionnel)" htmlFor="c-topic" hint="Par défaut smart-energy-control/sensors/<sensor_id>/temperature"><input id="c-topic" className="mono" value={topic} onChange={(e) => setTopic(e.target.value)} /></Field>
          </div>
        )}
        <button className="btn btn-primary" disabled={busy || !ok}>Créer</button>
        <p className="small muted">Une création enregistrée ne garantit pas que le module répond : l’état matériel s’affiche ensuite dans <Link to="/">le tableau de bord</Link>.</p>
      </form>
    </Card>
  );
}
